import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, TrendingUp, Play, Square, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const GPS_INTERVAL_MS = 60000; // 60 seconds

const MyShiftPage = () => {
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle | tracking | error
  const pollingRef = useRef(null);
  const shiftRef = useRef(null);

  // Keep shiftRef in sync so the interval closure always has latest shift_id
  useEffect(() => {
    shiftRef.current = shift;
  }, [shift]);

  const fetchTodayShift = async () => {
    try {
      setLoading(true);
      const { data } = await staffSelfAPI.getTodayShift();
      setShift(data);
      return data;
    } catch (error) {
      if (error.response?.status === 404) {
        setShift(null);
      } else {
        console.error('Error fetching shift:', error);
        toast.error(getApiErrorMessage(error, 'Failed to load shift data'));
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const sendLocation = useCallback(async () => {
    const currentShift = shiftRef.current;
    if (!currentShift?.shift_id || currentShift.status !== 'IN_PROGRESS') return;

    if (!navigator.geolocation) {
      setGpsStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await staffSelfAPI.sendLocation({
            shift_id: currentShift.shift_id,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy || null,
            speed: pos.coords.speed || null,
            battery_level: null,
          });
          setGpsStatus('tracking');
          // Refresh distance display without full reload
          const { data } = await staffSelfAPI.getTodayShift();
          setShift(data);
        } catch (err) {
          console.error('GPS ping failed:', err);
          setGpsStatus('error');
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        setGpsStatus('error');
      },
      { timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return; // already running
    sendLocation(); // fire immediately on shift start
    pollingRef.current = setInterval(sendLocation, GPS_INTERVAL_MS);
  }, [sendLocation]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setGpsStatus('idle');
  }, []);

  // Start/stop polling based on shift status
  useEffect(() => {
    if (shift?.status === 'IN_PROGRESS') {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [shift?.status, startPolling, stopPolling]);

  useEffect(() => {
    fetchTodayShift();
  }, []);

  const handleStartShift = async () => {
    try {
      setActionLoading(true);
      await staffSelfAPI.startShift({});
      const data = await fetchTodayShift();
      if (data?.status === 'IN_PROGRESS') {
        toast.success('Shift started! GPS tracking is now active.');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to start shift'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndShift = async () => {
    try {
      setActionLoading(true);
      stopPolling();
      await staffSelfAPI.endShift({});
      await fetchTodayShift();
      toast.success('Shift ended successfully!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to end shift'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
          <p className="text-gray-600 text-sm">Loading shift data...</p>
        </div>
      </div>
    );
  }

  const shiftStatus = shift?.status || 'NOT_STARTED';
  const isActive = shiftStatus === 'IN_PROGRESS';
  const isEnded = shiftStatus === 'ENDED';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Shift</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your daily work shift</p>
      </div>

      {/* Shift Status Card */}
      <Card className={`border-2 ${
        isActive ? 'border-green-400 bg-green-50' :
        isEnded ? 'border-gray-400 bg-gray-50' :
        'border-blue-400 bg-blue-50'
      }`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="text-base sm:text-lg">Today's Shift Status</span>
            <div className="flex items-center gap-2">
              {isActive && (
                <span className="flex items-center gap-1 text-xs font-medium text-green-700">
                  {gpsStatus === 'tracking' ? (
                    <><Wifi className="w-3.5 h-3.5" /> GPS Active</>
                  ) : gpsStatus === 'error' ? (
                    <><WifiOff className="w-3.5 h-3.5 text-red-500" /><span className="text-red-600">GPS Error</span></>
                  ) : (
                    <><Wifi className="w-3.5 h-3.5 animate-pulse" /> Connecting...</>
                  )}
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                isActive ? 'bg-green-500 text-white' :
                isEnded ? 'bg-gray-500 text-white' :
                'bg-blue-500 text-white'
              }`}>
                {isActive ? 'ACTIVE' : isEnded ? 'ENDED' : 'NOT STARTED'}
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 sm:p-3 rounded-lg bg-white">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Start Time</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {shift?.start_time ? new Date(shift.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 sm:p-3 rounded-lg bg-white">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">End Time</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {shift?.end_time ? new Date(shift.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 sm:p-3 rounded-lg bg-white">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Distance Today</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {shift?.total_distance_km ? `${Number(shift.total_distance_km).toFixed(2)} km` : '0 km'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 sm:p-3 rounded-lg bg-white">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Visits Completed</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {shift?.visits_count || 0}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent>
          {!shift || shiftStatus === 'NOT_STARTED' ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Ready to start your shift?</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Click the button below to start tracking your work for today. GPS location will be sent automatically every 60 seconds.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleStartShift}
                disabled={actionLoading}
                className="w-full h-14 text-base sm:text-lg font-semibold bg-green-600 hover:bg-green-700"
              >
                <Play className="w-5 h-5 mr-2" />
                {actionLoading ? 'Starting...' : 'Start Shift'}
              </Button>
            </div>
          ) : isActive ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Shift is active</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Your location is being sent every 60 seconds. End your shift when you're done for the day.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleEndShift}
                disabled={actionLoading}
                variant="destructive"
                className="w-full h-14 text-base sm:text-lg font-semibold"
              >
                <Square className="w-5 h-5 mr-2" />
                {actionLoading ? 'Ending...' : 'End Shift'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Shift ended</p>
                  <p className="text-xs text-gray-600 mt-1">
                    You have completed your shift for today. See you tomorrow!
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyShiftPage;
